/**
 * SHELL — the browser network client (Shell owns this; it is NOT in src/backend).
 *
 * Talks to Backend's Cloudflare Pages Functions using the @/types contracts
 * verbatim:
 *   - POST /api/submit            body ScoreSubmission -> ScoreSubmissionResult
 *   - GET  /api/leaderboard?date=&scope=global|friends -> LeaderboardPage
 *
 * Identity is the Supabase JWT in `Authorization: Bearer <token>` — never in the
 * body. These endpoints may return 501 until Backend lands, and the device may be
 * offline, so EVERY call returns a discriminated Result the UI renders as
 * ok / pending(501) / offline / unauthorized / error — it never throws.
 */

import { PROTOCOL_VERSION } from '@/types';
import type {
  DateKey,
  LeaderboardPage,
  LeaderboardScope,
  ReplayData,
  ScoreSubmission,
  ScoreSubmissionResult,
} from '@/types';
import { API_BASE, CLIENT_VERSION, detectPlatform } from '../config';
import type { AuthClient } from './auth';

/** Why a call did not return data. `pending` == server not implemented (501). */
export type NetFailure =
  | { readonly kind: 'pending'; readonly status: 501 }
  | { readonly kind: 'offline' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'rate_limited' }
  | { readonly kind: 'server'; readonly status: number }
  | { readonly kind: 'error'; readonly message: string };

export type NetResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly failure: NetFailure };

const ok = <T>(data: T): NetResult<T> => ({ ok: true, data });
const fail = <T>(failure: NetFailure): NetResult<T> => ({ ok: false, failure });

/** Human-readable, spoiler-free message for a failure (for banners). */
export function describeFailure(f: NetFailure): string {
  switch (f.kind) {
    case 'pending':
      return 'Leaderboards are warming up. Your run is saved and will sync soon.';
    case 'offline':
      return "You're offline. Your run is saved locally and will submit when you reconnect.";
    case 'unauthorized':
      return 'Your session expired. Reload to keep your streak in sync.';
    case 'rate_limited':
      return 'Too many requests — please wait a moment and try again.';
    case 'server':
      return `The server had a hiccup (${f.status}). We'll retry automatically.`;
    case 'error':
      return f.message;
  }
}

export interface NetworkClient {
  submit(replay: ReplayData): Promise<NetResult<ScoreSubmissionResult>>;
  leaderboard(dateKey: DateKey, scope: LeaderboardScope): Promise<NetResult<LeaderboardPage>>;
}

interface ClientDeps {
  readonly auth: AuthClient;
  /** Injectable for tests; defaults to global fetch. */
  readonly fetchImpl?: typeof fetch;
  /** Injectable connectivity check; defaults to navigator.onLine. */
  readonly isOnline?: () => boolean;
}

function buildSubmission(replay: ReplayData): ScoreSubmission {
  return {
    protocolVersion: PROTOCOL_VERSION,
    dateKey: replay.dateKey,
    seed: replay.seed,
    score: replay.metrics.score,
    replay,
    client: {
      clientVersion: CLIENT_VERSION,
      protocolVersion: PROTOCOL_VERSION,
      platform: detectPlatform(),
      userAgent: navigator.userAgent,
      timezoneOffsetMin: new Date().getTimezoneOffset(),
      submittedAt: new Date().toISOString(),
    },
  };
}

/** Map a thrown/HTTP condition to a typed failure. */
function classifyHttp(status: number): NetFailure {
  if (status === 501) return { kind: 'pending', status: 501 };
  if (status === 401 || status === 403) return { kind: 'unauthorized' };
  if (status === 429) return { kind: 'rate_limited' };
  return { kind: 'server', status };
}

export function createNetworkClient(deps: ClientDeps): NetworkClient {
  const doFetch = deps.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const online = deps.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine));

  async function authedHeaders(base: Record<string, string>): Promise<Record<string, string>> {
    const token = await deps.auth.getToken();
    return token ? { ...base, authorization: `Bearer ${token}` } : base;
  }

  async function request<T>(path: string, init: RequestInit): Promise<NetResult<T>> {
    if (!online()) return fail<T>({ kind: 'offline' });
    let res: Response;
    try {
      res = await doFetch(`${API_BASE}${path}`, init);
    } catch (err) {
      // A network-layer throw is indistinguishable from offline in practice.
      const message = err instanceof Error ? err.message : 'Network request failed';
      return online() ? fail<T>({ kind: 'error', message }) : fail<T>({ kind: 'offline' });
    }

    if (res.status === 501) return fail<T>({ kind: 'pending', status: 501 });
    if (!res.ok) return fail<T>(classifyHttp(res.status));

    try {
      const data = (await res.json()) as T;
      return ok(data);
    } catch {
      return fail<T>({ kind: 'error', message: 'Malformed response from server.' });
    }
  }

  return {
    async submit(replay) {
      const body = JSON.stringify(buildSubmission(replay));
      const headers = await authedHeaders({ 'content-type': 'application/json' });
      const result = await request<ScoreSubmissionResult>('/api/submit', {
        method: 'POST',
        headers,
        body,
      });
      // A 200 that says "rejected" is still a transport success; the caller
      // inspects result.data.status. We only translate transport failures.
      return result;
    },

    async leaderboard(dateKey, scope) {
      const params = new URLSearchParams({ date: dateKey, scope });
      const headers = await authedHeaders({});
      return request<LeaderboardPage>(`/api/leaderboard?${params.toString()}`, {
        method: 'GET',
        headers,
      });
    },
  };
}

export { buildSubmission };
